import { useEffect, useRef, useState } from 'react';
import { Send, Loader2, CheckCircle2, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import type { SmsChatMessage } from './use-sms-chat';

interface SmsChatProps {
  messages: SmsChatMessage[];
  isLoading: boolean;
  done: boolean;
  onSend: (text: string) => void;
  onReset: () => void;
}

/**
 * The message thread + reply box for the SMS-agent test tool. Mirrors the
 * scheduling chat: user turns right, agent turns left, a loading bubble while
 * the agent is thinking, Enter-to-send. When the agent ends the conversation the
 * reply box is replaced with a "conversation ended" banner + a restart button.
 */
export function SmsChat({
  messages,
  isLoading,
  done,
  onSend,
  onReset,
}: SmsChatProps): React.ReactElement {
  const [input, setInput] = useState('');
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading]);

  const handleSend = (): void => {
    if (!input.trim()) return;
    onSend(input);
    setInput('');
  };

  return (
    <div className="flex h-full flex-col">
      <div className="flex-1 space-y-4 overflow-y-auto p-4">
        {messages.map((msg, idx) => (
          <div
            key={idx}
            className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-[85%] whitespace-pre-wrap rounded-lg px-4 py-2.5 text-sm ${
                msg.role === 'user' ? 'bg-primary text-primary-foreground' : 'bg-muted'
              }`}
            >
              {msg.content}
            </div>
          </div>
        ))}
        {isLoading && (
          <div className="flex justify-start">
            <div className="flex items-center gap-2 rounded-lg bg-muted px-4 py-2.5">
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              <span className="text-sm text-muted-foreground">The agent is typing…</span>
            </div>
          </div>
        )}
        <div ref={endRef} />
      </div>

      {done ? (
        <div className="flex items-center justify-between gap-3 border-t bg-muted/30 px-4 py-3">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <CheckCircle2 className="h-4 w-4 text-green-600" />
            Conversation ended.
          </div>
          <Button variant="outline" size="sm" onClick={onReset} className="gap-1.5">
            <RotateCcw className="h-3.5 w-3.5" />
            New conversation
          </Button>
        </div>
      ) : (
        <div className="flex gap-2 border-t p-3">
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
            placeholder="Reply as the patient…"
            disabled={isLoading}
            maxLength={500}
            autoFocus
          />
          <Button onClick={handleSend} disabled={!input.trim() || isLoading} size="icon">
            <Send className="h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  );
}
